import clsx from 'clsx'
import isEmpty from 'lodash/isEmpty'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'

import SvgCheck from '@Assets/SVG/Check'
import { FormElementProps, FormState } from '@Shared/Components/Forms/FormElement.types'
import { useFormContext } from '@Shared/Components/Forms/useFormContext'

import styles from './FormInput.module.scss'

type GetStringValueType<T> = (value: T | null) => string
type GetObjectValueType<T> = (value: string) => T | null
export interface FormInputProps<T> extends FormElementProps {
  autoComplete?: string
  defaultValidate?: boolean
  errorClassName?: string
  getObjectValue?: GetStringValueType<T>
  label?: string
  maxLength?: number
  onError?: (error?: string) => void
  type?: string
  valueModifier?: GetObjectValueType<T>
  withoutWhiteSpace?: boolean
}
/**
 * @deprecated The const should not be used
 */
export const FormInputDeprecated: FC<FormInputProps<unknown>> = ({
  autoComplete,
  className,
  defaultValidate = true,
  disabled,
  errorClassName,
  getObjectValue,
  id,
  label,
  maxLength,
  modelKey,
  onError,
  startTouched = false,
  triggersValidation,
  type,
  validations,
  valueModifier,
  withoutWhiteSpace,
}) => {
  const ref = useRef<HTMLInputElement | null>(null)
  const [pasted, setPasted] = useState(false)
  const { runValidation, submitAttempts, updateValue, validateOnBlur, validation, value } = useFormContext(modelKey, validations)
  const [hasErrors, setHasErrors] = useState<boolean>(validation && validation.validity === FormState.ERROR)
  const [touched, setTouched] = useState(startTouched)
  const callOnError = (err: string | undefined) => {
    if (onError) {
      onError(err)
    }
  }

  useEffect(() => {
    if (isEmpty(triggersValidation) || disabled) {
      return
    }
    if (isEmpty(value)) {
      setTouched(false)
    } else if (!isEmpty(value)) {
      setTouched(true)
      runValidation(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, triggersValidation)

  useEffect(() => {
    if (submitAttempts >= 1) {
      setTouched(true)
      setHasErrors(validation && validation.validity === 'ERROR')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitAttempts])

  useEffect(() => {
    setHasErrors(validation && validation.validity === 'ERROR')
  }, [validation])

  useEffect(() => {
    if (defaultValidate && !isEmpty(value)) {
      setTouched(true)
    } else if (!defaultValidate) {
      setTouched(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValidate])

  const handleInputChange = (event) => {
    setTouched(false)
    const currentValue: string = event.target.value.replace(/\s\s+/g, ' ').trimStart()
    const newValue = withoutWhiteSpace ? currentValue.replace(/\s+/g, '') : currentValue
    const currValue = (getObjectValue ? getObjectValue(value) : value) || ''

    const diffCount = newValue.split('').length > currValue.split('').length && (newValue.split('').length - currValue.split('').length) > 1
    setHasErrors(false)
    updateValue(valueModifier ? valueModifier(newValue) : newValue)
    if (diffCount && !pasted) {
      setTimeout(() => {
        setTouched(true)
        ref.current?.blur()
      }, 500)
    }
    setPasted(false)
  }

  const onFieldAutoUpdate = (event) => {
    if (event.target !== document.activeElement) {
      if (!isEmpty(event.target.value)) {
        setTouched(true)
      }
      runValidation(event.target.value)
    }
  }

  const error: string | null = useMemo(() => {
    if (!validation?.validationResponses) {
      return null
    }
    const foundError = validation.validationResponses.find(v => v.message && v.state === 'ERROR')
    if (foundError?.message) {
      callOnError(foundError.message)
      return foundError.message
    }
    return null
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validation])

  return (
    <div className={clsx(className, styles.formInputWrapper)}>
      <div className={clsx([styles.formInput, hasErrors && touched && error && errorClassName])}>
        <input
          autoComplete={autoComplete}
          className={clsx(
            touched && hasErrors && styles.error,
            touched && !hasErrors && styles.valid,
            !label && styles.withLabel,
          )}
          disabled={disabled}
          id={id}
          maxLength={maxLength}
          name={modelKey}
          onBlur={(e) => {
            const newValue = e.target.value.replace(/\s\s+/g, ' ').trim()
            updateValue(valueModifier ? valueModifier(newValue) : newValue)
            setTouched(true)
            validateOnBlur()
          }}
          onChange={handleInputChange}
          onInput={onFieldAutoUpdate}
          onPaste={() => setPasted(true)}
          ref={ref}
          tabIndex={disabled ? -1 : undefined}
          type={type}
          value={getObjectValue ? getObjectValue(value || '') : value || ''}
        />
        {label
            && (
              <p
                className={clsx(
                  styles.label,
                  disabled && styles.disabled,
                  !isEmpty(value) && styles.hasValue,
                  touched && !hasErrors && styles.validLabel,
                )}
              >
                {label}
              </p>
            )}
        {touched && !hasErrors && <div className={styles.checkmark}><SvgCheck /></div>}
      </div>
      {hasErrors && touched && error && (<p key={error} className={styles.errorCode}>{error}</p>)}
    </div>
  )
}
